import flagCivicPapers from "@/assets/flag-civic-papers.svg";
import flagDangerous from "@/assets/flag-dangerous.svg";
import flagGangMembers from "@/assets/flag-gang-members.svg";
import flagInJail from "@/assets/flag-in-jail.svg";
import flagMedical from "@/assets/flag-medical.svg";
import flagNotes from "@/assets/flag-notes.svg";
import flagWarrants from "@/assets/flag-warrants.svg";
import { useClickOutside } from "@/hooks/useClickOutside";
import React, { useRef, useState, useEffect } from "react";
import { FlagContainer, FlagDetails } from "./styles";
import Popover from "@/common/elements/Popover";
import Button from "@/common/elements/Button";
import { CloseOutlined, DeleteOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { setDeleteAPis } from "@/slices/bookingsSlice";
import API_ENDPOINTS from "@/util/api";
import { Popconfirm } from "antd";
import { setBookings } from "@/slices/bookingsSlice";
import {
  getRefListItemsbyName,
} from "@/util/common_function";

export const INMATE_FLAG_TYPE = {
  WARRANT: "FLAGTYPES_WARRANTS",
  CIVIC: "CIVIL_PAPER_STATUS",
  DANGER: "FLAGTYPES_DANGER",
  DOCUMENTS: "FLAGTYPES_DOCUMENTS",
  INJAIL: "FLAGTYPES_INJAIL",
  GANG: "FLAGTYPES_GANG",
  MEDICAL: "FLAGTYPES_MEDICAL",
  NOTES: "FLAGTYPES_NOTES",
};

export const getFlagIcon = (type) => {
  switch (type) {
    case INMATE_FLAG_TYPE.WARRANT:
      return flagWarrants;
    case INMATE_FLAG_TYPE.CIVIC:
      return flagCivicPapers;
    case INMATE_FLAG_TYPE.DANGER:
      return flagDangerous;
    case INMATE_FLAG_TYPE.INJAIL:
      return flagInJail;
    case INMATE_FLAG_TYPE.GANG:
      return flagGangMembers;
    case INMATE_FLAG_TYPE.MEDICAL:
      return flagMedical;
    case INMATE_FLAG_TYPE.NOTES:
      return flagNotes;
    default:
      return null;
  }
};

export const getFlagName = (type) => {
  switch (type) {
    case INMATE_FLAG_TYPE.WARRANT:
      return "Warrants";
    case INMATE_FLAG_TYPE.CIVIC:
      return "Civil";
    case INMATE_FLAG_TYPE.DANGER:
      return "Danger";
    case INMATE_FLAG_TYPE.INJAIL:
      return "In Jail";
    case INMATE_FLAG_TYPE.GANG:
      return "Gang";
    case INMATE_FLAG_TYPE.MEDICAL:
      return "Medical";
    case INMATE_FLAG_TYPE.NOTES:
      return "Notes";
    default:
      return "";
  }
};

const InmateFlag = ({
  type = INMATE_FLAG_TYPE.WARRANT,
  flagEnabled = false,
  isSkew = true,
  cdInvolvements = [],
  isEdit = false,
}) => {
  const [detailsVisible, setDetailsVisible] = useState(false);
  const flagRef = useRef();
  const dispatch = useDispatch();
  const bookings = useSelector((state) => state?.bookings?.bookings);
  // Maintain a local state for the involvement list.
  const [localInvolvements, setLocalInvolvements] = useState(cdInvolvements);

  // Update local state if the prop changes.
  useEffect(() => {
    setLocalInvolvements(cdInvolvements);
  }, [cdInvolvements]);

  useClickOutside(flagRef, () => setDetailsVisible(false));

  const onFlagClick = () => {
    // setDetailsVisible((prev) => !prev);
  };

  const handleOpenChange = (newOpen) => {
    setDetailsVisible(newOpen);
  };

  // Helper: return additional info for a given item.
  const getAdditionalInfo = (item) => {
    if (!type) return "";
  
    const parts = type.split("_");
    if (parts.length < 2) return "";
    let flagKey = parts[1].toLowerCase(); // e.g. "danger", "gang", "medical", "notes", etc.
    flagKey = flagKey === "notes" ? "note" : flagKey;
    const fieldName = `${flagKey}TypeId`;
    if (!item.hasOwnProperty(fieldName)) return "";
  
    // Construct the reference list name. For example, "danger" becomes "DANGER_Type".
    const refListName = `${flagKey.toUpperCase()}_Type`;

    // Retrieve the reference items for this list.
    const refItems = getRefListItemsbyName(refListName) || [];

    // Find the matching reference item using the involvement's type id.
    const match = refItems.find((r) => r.refListItemGlobalId === item[fieldName]);
    return match ? (match.defaultString || match.longString || "") : "";
  };

  // Inner component: displays flag details and per-item delete.
  const FlagDetailsContent = ({ type, cdInvolvements }) => {
    const deleteInvolvement = (invId) => {
      // Build the deletion endpoint URL. Adjust this based on your API design.
      const url = API_ENDPOINTS.jacketFlags + invId;
      dispatch(setDeleteAPis(url));

      // Remove the involvement from local state.
      const updatedInvolvements = localInvolvements.filter(
        (item) => item.invId !== invId
      );
      setLocalInvolvements(updatedInvolvements);

      const updatedBookings = {
        ...bookings,
        cdInvolvements: bookings.cdInvolvements.filter(
          (item) => item.invId !== invId
        ),
      };

      // Dispatch the updated bookings object.
      dispatch(setBookings(updatedBookings));
    };

    return (
      <FlagDetails
        className={`flag-content ${getFlagName(type)
          ?.toLowerCase()
          ?.split(" ")
          ?.join("-")}`}
      >
        <div
          className="flag-type-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h5>
            {getFlagName(type)} ({localInvolvements.length})
          </h5>
          <Button
            type="default"
            variant="default"
            icon={<CloseOutlined style={{ color: "#ffffff" }} />}
            onClick={onFlagClick}
          />
        </div>
        <ul>
          {localInvolvements.map((item) => (
            <React.Fragment key={item.invId}>
              <li className="item">
                <div
                  className="header"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>{getAdditionalInfo(item) || ""}</span>
                  {isEdit && (
                    <Popconfirm
                      title="Delete this flag?"
                      onConfirm={() => deleteInvolvement(item.invId)}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button
                        type="default"
                        variant="default"
                        icon={<DeleteOutlined />}
                        onClick={() => deleteInvolvement(item.invId)}
                      />
                    </Popconfirm>
                  )}
                </div>
                <div className="content">{item.invDesc}</div>
              </li>
              <hr />
            </React.Fragment>
          ))}
        </ul>
      </FlagDetails>
    );
  };

  return (
    <>
      <FlagContainer id={type} key={type} ref={flagRef} skew={isSkew}>
        <button
          type="button"
          className={`flag-btn ${getFlagName(type)
            ?.toLowerCase()
            ?.split(" ")
            ?.join("-")} ${flagEnabled ? "enabled" : ""}`}
          onClick={onFlagClick}
        >
          <Popover
            className="unstyle-padding"
            trigger={["hover", "click"]}
            open={detailsVisible}
            onOpenChange={handleOpenChange}
            content={
              <FlagDetailsContent
                type={type}
                cdInvolvements={localInvolvements}
              />
            }
            placement="bottom"
          >
            <div className="flag">
              <img
                alt={`flag ${type} icon`}
                src={getFlagIcon(type)}
                className="flag-main-icon"
              />
              <div className="flag-info">
                <span className="flag-name">{getFlagName(type)}</span>
                <span className="count">{localInvolvements.length}</span>
              </div>
            </div>
          </Popover>
        </button>
      </FlagContainer>
    </>
  );
};

export default InmateFlag;
